/**
 * FEATURE PAGE: The Living War Room
 * Dedicated SEO-optimized landing page for the Living War Room feature.
 * Route: /tools/living-war-room
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Flame, ArrowRight, Sparkles, Shield, Zap, MessageSquare,
  Play, Users, Target, ChevronDown, CheckCircle
} from 'lucide-react';

const GOD_CARDS = [
  { name: 'Athena', emoji: '🦉', title: 'Strategic Intelligence', desc: 'Scans competitive data and identifies threats before they materialize.', gradient: 'from-amber-500 to-orange-600' },
  { name: 'Hermes', emoji: '👟', title: 'Content God', desc: 'Crafts messaging that converts — headlines, hooks, viral angles.', gradient: 'from-orange-500 to-red-600' },
  { name: 'Apollo', emoji: '☀️', title: 'Distribution Oracle', desc: 'Predicts optimal timing and channels with pattern-based precision.', gradient: 'from-yellow-500 to-orange-500' },
  { name: 'Ares', emoji: '🗡️', title: 'Competitive Warrior', desc: 'Advocates for aggressive, decisive action when the market opens.', gradient: 'from-red-600 to-rose-700' },
  { name: 'Plutus', emoji: '💰', title: 'The CFO', desc: 'Keeps every argument grounded in ROI and financial reality.', gradient: 'from-green-500 to-emerald-600' },
  { name: 'Eris', emoji: '⚔️', title: 'The Contrarian', desc: 'Challenges every assumption — the one who stress-tests your plan.', gradient: 'from-red-600 to-pink-600' },
];

const DEBATE_EXAMPLES = [
  '"Cold outreach vs. content marketing — which scales faster in 2025?"',
  '"Should we go product-led or sales-led growth right now?"',
  '"We have $50k. Do we spend it on ads or SEO?"',
  '"Is our brand voice hurting or helping conversions?"',
  '"Are we expanding too fast or not fast enough?"',
];

const FeaturePageWarRoom = () => {
  const [activeExample, setActiveExample] = useState(0);

  return (
    <div className="min-h-screen bg-[#030305] text-white">
      {/* Ambient BG */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-0 left-1/3 w-[700px] h-[700px] bg-purple-600/5 rounded-full blur-[250px]" />
        <div className="absolute bottom-0 right-1/3 w-[700px] h-[700px] bg-red-600/5 rounded-full blur-[250px]" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(rgba(255,255,255,0.15) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
      </div>

      <div className="relative z-10">
        {/* Nav */}
        <nav className="flex items-center justify-between max-w-6xl mx-auto px-6 py-5">
          <Link to="/" className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-purple-400" />
            <span className="font-bold text-white">iSupplyAI</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/replays" className="text-sm text-gray-400 hover:text-white transition-colors">View Replays</Link>
            <Link to="/" className="px-4 py-2 rounded-xl bg-gradient-to-r from-purple-600 to-red-600 text-sm font-semibold hover:opacity-90 transition-opacity">
              Try It Free
            </Link>
          </div>
        </nav>

        {/* Hero */}
        <section className="max-w-5xl mx-auto px-6 pt-16 pb-24 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500/10 border border-red-500/20 text-red-400 text-xs font-medium mb-6">
            <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
            LIVE 24/7
          </div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black leading-[1.05] mb-6">
            The Living
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-red-400 to-amber-400">War Room</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-10 leading-relaxed">
            12 AI gods with distinct personalities debate your exact business strategy — live, personalized, and brutally honest.
            No generic advice. No hedging. Real arguments.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/"
              className="flex items-center gap-2 px-8 py-4 rounded-2xl bg-gradient-to-r from-purple-600 to-red-600 text-white font-bold text-base hover:from-purple-500 hover:to-red-500 transition-all shadow-xl shadow-purple-500/20"
              data-testid="war-room-cta-hero"
            >
              <Flame className="w-5 h-5" /> Enter The War Room
            </Link>
            <Link
              to="/replays"
              className="flex items-center gap-2 px-8 py-4 rounded-2xl bg-gray-800/60 border border-gray-700/50 hover:border-purple-500/30 text-white text-base transition-all"
              data-testid="war-room-watch-replays"
            >
              <Play className="w-4 h-4 text-purple-400" /> Watch Replays
            </Link>
          </div>
          <p className="text-xs text-gray-600 mt-5">Free to start. No credit card. 12 gods ready.</p>
        </section>

        {/* What it does */}
        <section className="max-w-5xl mx-auto px-6 pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Mock debate window */}
            <div className="rounded-2xl border border-purple-500/20 bg-gray-900/80 overflow-hidden shadow-2xl shadow-purple-500/10">
              <div className="px-4 py-3 border-b border-gray-700/50 bg-gray-900/60 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <span className="text-xs font-semibold text-white">The Living War Room</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-500/20 text-green-400 border border-green-500/30 ml-auto">LIVE</span>
              </div>
              <div className="px-4 py-3 bg-purple-900/20 border-b border-purple-500/10">
                <p className="text-xs text-purple-300/70 italic">
                  "{DEBATE_EXAMPLES[activeExample]}"
                </p>
              </div>
              <div className="p-4 space-y-3 min-h-[200px]">
                {[
                  { god: '🦉', name: 'Athena', color: 'text-amber-400', text: 'The data shows content compounds at 18 months. Outreach gives you leads in 2 weeks. You need both — but in what order depends on your runway.' },
                  { god: '⚔️', name: 'Eris', color: 'text-red-400', text: "That's the safest answer possible, Athena. What's your burn rate? Because 'both' isn't a strategy, it's paralysis dressed as balance." },
                  { god: '☀️', name: 'Apollo', color: 'text-yellow-400', text: '...There is a 60-day window forming in this market. The timing of this debate is not coincidental.' },
                ].map((m, i) => (
                  <div key={i} className="flex items-start gap-2.5">
                    <div className="w-7 h-7 rounded-full bg-gray-700 flex items-center justify-center flex-shrink-0 text-sm">{m.god}</div>
                    <div className="flex-1">
                      <span className={`text-[10px] font-semibold ${m.color}`}>{m.name}</span>
                      <p className="text-gray-300 text-xs leading-relaxed mt-0.5">{m.text}</p>
                    </div>
                  </div>
                ))}
              </div>
              {/* Debate topic switcher */}
              <div className="px-4 pb-3 flex gap-1 flex-wrap">
                {DEBATE_EXAMPLES.map((_, i) => (
                  <button key={i} onClick={() => setActiveExample(i)} className={`w-1.5 h-1.5 rounded-full transition-colors ${i === activeExample ? 'bg-purple-400' : 'bg-gray-700 hover:bg-gray-600'}`} />
                ))}
                <span className="text-[10px] text-gray-600 ml-2">Example debates</span>
              </div>
            </div>

            <div>
              <h2 className="text-3xl font-black mb-6">The boardroom you never had access to</h2>
              <div className="space-y-5">
                {[
                  { icon: <Flame className="w-4 h-4 text-red-400" />, title: 'Real arguments, not summaries', desc: 'Gods challenge each other, cite data, and form alliances. The debate is the strategy.' },
                  { icon: <Target className="w-4 h-4 text-amber-400" />, title: 'Your business, your debate', desc: 'Submit any strategic question and get a council tailored to your exact situation.' },
                  { icon: <Shield className="w-4 h-4 text-blue-400" />, title: 'A verdict you can act on', desc: 'Every session ends with a consensus verdict, primary action item, and the next debate topic.' },
                  { icon: <Zap className="w-4 h-4 text-green-400" />, title: 'Export & share the replay', desc: 'Download the debate as a .webm video or share a public replay link with your team.' },
                ].map((f, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gray-800/60 border border-gray-700/40 flex items-center justify-center flex-shrink-0">{f.icon}</div>
                    <div>
                      <p className="text-sm font-semibold text-white">{f.title}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Meet the Gods */}
        <section className="max-w-5xl mx-auto px-6 pb-24">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-black mb-3">Meet The Council</h2>
            <p className="text-gray-500 text-sm">Each god has a distinct personality, biases, and expertise. They don't agree.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {GOD_CARDS.map((god) => (
              <div key={god.name} className="bg-gray-900/60 border border-gray-700/40 rounded-xl p-4 hover:border-purple-500/30 transition-colors">
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${god.gradient} flex items-center justify-center text-xl mb-3 shadow-lg`}>
                  {god.emoji}
                </div>
                <p className="text-sm font-bold text-white">{god.name}</p>
                <p className="text-[10px] text-gray-500 mb-2">{god.title}</p>
                <p className="text-xs text-gray-400 leading-relaxed">{god.desc}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-gray-600 mt-4">+ 6 more gods in the full council — 12 total</p>
        </section>

        {/* How it works */}
        <section className="max-w-4xl mx-auto px-6 pb-24">
          <h2 className="text-3xl font-black text-center mb-10">How It Works</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              { num: '01', title: 'Enter the Pantheon', desc: 'Sign in and navigate to The Pantheon. The gods are always ready — no setup required.' },
              { num: '02', title: 'Issue The Challenge', desc: 'Type your strategic question or let the AI surface what needs debating most from your metrics.' },
              { num: '03', title: 'Watch & Steer', desc: 'The gods debate in real time. Jump in to redirect. Side with a god. Share the replay.' },
            ].map((step) => (
              <div key={step.num} className="text-center">
                <div className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-500 to-red-500 mb-3">{step.num}</div>
                <h3 className="text-base font-bold text-white mb-2">{step.title}</h3>
                <p className="text-xs text-gray-500 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <section className="max-w-3xl mx-auto px-6 pb-20 text-center">
          <div className="rounded-2xl border border-purple-500/20 bg-gradient-to-b from-purple-900/20 to-gray-900/50 p-12">
            <h2 className="text-3xl font-black mb-4">Start Your First Debate</h2>
            <p className="text-gray-400 text-sm mb-8 max-w-sm mx-auto">
              12 AI gods are assembled and waiting. What strategic question is keeping you up at night?
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-10 py-4 rounded-2xl bg-gradient-to-r from-purple-600 to-red-600 text-white font-bold hover:from-purple-500 hover:to-red-500 transition-all shadow-xl shadow-purple-500/20"
              data-testid="war-room-final-cta"
            >
              <Flame className="w-5 h-5" /> Enter The War Room Free
            </Link>
            <div className="flex items-center justify-center gap-4 mt-5 text-xs text-gray-600">
              <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-green-500" /> No credit card</span>
              <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-green-500" /> Free to start</span>
              <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-green-500" /> Instant access</span>
            </div>
          </div>
        </section>

        <div className="text-center pb-10 text-[10px] text-gray-700">
          iSupplyAI — 12 AI Gods. One Strategy. &nbsp;·&nbsp; <Link to="/tools/strategy-score" className="hover:text-gray-500">Strategy Score</Link> &nbsp;·&nbsp; <Link to="/replays" className="hover:text-gray-500">Replays</Link>
        </div>
      </div>
    </div>
  );
};

export default FeaturePageWarRoom;
